'use client';
import { RefObject, useEffect, useState } from 'react';
import { loadScriptOnce } from '../../utils/loadScriptOnce';
import {
  PREFERRED_SOURCE_RENDER_POLL_MS,
  PREFERRED_SOURCE_RENDER_TIMEOUT_MS,
  PREFERRED_SOURCE_ROOT_MARGIN,
  PREFERRED_SOURCE_SCRIPT,
} from './constants';

export type PreferredSourceButtonStatus =
  | 'idle'
  | 'loading'
  | 'rendered'
  | 'failed';

type PreferredSourceApi = { init: () => void };

/**
 * `publisher.js` exposes a callback queue: before it loads the array buffers
 * callbacks, afterwards `push` runs them immediately.
 */
type PreferredSourceWindow = Window & {
  PREFERRED_SOURCE?: { push: (cb: (api: PreferredSourceApi) => void) => void };
};

/** publisher.js marks the placeholder, then draws into its shadow root; a closed root only shows as height. */
export const hasRenderedButton = (placeholder: HTMLElement) =>
  placeholder.getAttribute('data-initialized') === 'true' &&
  ((placeholder.shadowRoot?.childElementCount ?? 0) > 0 ||
    placeholder.getBoundingClientRect().height > 0);

/**
 * Loads Google's publisher script once the container is near the viewport and
 * reports whether the button actually rendered into the placeholder.
 */
export const usePreferredSourceButton = (
  containerRef: RefObject<HTMLElement | null>,
  placeholderRef: RefObject<HTMLElement | null>,
): PreferredSourceButtonStatus => {
  const [status, setStatus] = useState<PreferredSourceButtonStatus>('idle');

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStatus('loading');
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        setStatus('loading');
      },
      { rootMargin: PREFERRED_SOURCE_ROOT_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    if (status !== 'loading') return;
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;
    let deadline: ReturnType<typeof setTimeout> | undefined;

    const stop = () => {
      if (poll) clearInterval(poll);
      if (deadline) clearTimeout(deadline);
    };

    loadScriptOnce(PREFERRED_SOURCE_SCRIPT)
      .then(() => {
        if (cancelled) return;
        const w = window as PreferredSourceWindow;
        // The script scans placeholders only when it loads; a placeholder
        // mounted later (client-side navigation) needs an explicit `init()`.
        w.PREFERRED_SOURCE =
          w.PREFERRED_SOURCE ??
          ([] as unknown as PreferredSourceWindow['PREFERRED_SOURCE']);
        w.PREFERRED_SOURCE?.push((api) => api.init());

        const check = () => {
          const placeholder = placeholderRef.current;
          if (placeholder && hasRenderedButton(placeholder)) {
            stop();
            setStatus('rendered');
          }
        };
        check();
        poll = setInterval(check, PREFERRED_SOURCE_RENDER_POLL_MS);
        deadline = setTimeout(() => {
          stop();
          setStatus((s) => (s === 'loading' ? 'failed' : s));
        }, PREFERRED_SOURCE_RENDER_TIMEOUT_MS);
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [status, placeholderRef]);

  return status;
};
